import { apiClient } from "./apiClient";

const BASE_TASKS = "/tasks";
const BASE_PROJECTS = "/tasks/projects";

// Projects

export async function createProjectApi({ token, workspaceId, data }) {
    // apiClient supports custom headers if the wrapper allows it, typically it handles token.
    // We need to pass workspaceId. Based on workspacesService, it's often in URL or body.
    // But my backend expects it in header or query. The other services use token in the arg object.
    // Let's assume apiClient handles the standard auth header. 
    // If we need custom headers like 'x-workspace-id', we might need to check apiClient implementation.
    // However, usually we can pass additional options or the service wrapper handles it.
    // Looking at workspacesService, it passes { token, method, body }.
    // If apiClient doesn't support custom headers easily, I might need to append it to query string?
    // Backend controller: `req.query.workspaceId || req.headers['x-workspace-id']`
    // So query param is safe.

    return apiClient(`${BASE_PROJECTS}?workspaceId=${workspaceId}`, {
        token,
        method: "POST",
        body: data,
    });
}

export async function listProjectsApi({ token, workspaceId }) {
    return apiClient(`${BASE_PROJECTS}?workspaceId=${workspaceId}`, {
        token,
        method: "GET",
    });
}

export async function updateProjectApi({ token, workspaceId, projectId, data }) {
    return apiClient(`${BASE_PROJECTS}/${projectId}`, {
        token,
        method: "PATCH",
        body: data,
    });
}

// Tasks

export async function createTaskApi({ token, workspaceId, data }) {
    // Task creation might need workspaceId if not in body, but tasks are linked to projects.
    // Project implicitly is in workspace. But controller might check workspace access.
    // Let's pass workspaceId in query strings generally if the backend looks for it.
    // Although tasks.controller.js createTask uses `req.user.id`. 
    // It doesn't seem to strictly need workspaceId if it trusts the projectId. 
    // But let's be consistent.

    return apiClient(BASE_TASKS, {
        token,
        method: "POST",
        body: data, // contains projectId
    });
}

export async function listTasksApi({ token, workspaceId, projectId }) {
    return apiClient(`${BASE_PROJECTS}/${projectId}/tasks`, {
        token,
        method: "GET",
    });
}

export async function updateTaskApi({ token, workspaceId, taskId, data }) {
    return apiClient(`${BASE_TASKS}/${taskId}`, {
        token,
        method: "PATCH",
        body: data,
    });
}

export async function reorderTasksApi({ token, workspaceId, updates }) {
    return apiClient(`${BASE_TASKS}/reorder`, {
        token,
        method: "PATCH",
        body: { updates }
    });
}

// Task Groups

export async function createTaskGroupApi({ token, workspaceId, data }) {
    return apiClient(`/tasks/task-groups`, {
        token,
        method: "POST",
        body: data, // { projectId, name }
    });
}

export async function listTaskGroupsApi({ token, workspaceId, projectId }) {
    return apiClient(`/tasks/projects/${projectId}/task-groups`, {
        token,
        method: "GET",
    });
}

export async function updateTaskGroupApi({ token, workspaceId, groupId, data }) {
    return apiClient(`/tasks/task-groups/${groupId}`, {
        token,
        method: "PATCH",
        body: data,
    });
}

export async function deleteTaskGroupApi({ token, workspaceId, groupId }) {
    return apiClient(`/tasks/task-groups/${groupId}`, {
        token,
        method: "DELETE"
    });
}

// Tags

export async function createTagApi({ token, workspaceId, data }) {
    // data: { name, color }
    return apiClient(`/tasks/tags?workspaceId=${workspaceId}`, {
        token,
        method: "POST",
        body: data
    });
}

export async function updateTagApi({ token, workspaceId, tagId, data }) {
    return apiClient(`/tasks/tags/${tagId}`, {
        token,
        method: "PATCH",
        body: data
    });
}

export async function deleteTagApi({ token, workspaceId, tagId }) {
    return apiClient(`/tasks/tags/${tagId}`, {
        token,
        method: "DELETE"
    });
}

export async function listTagsApi({ token, workspaceId }) {
    return apiClient(`/tasks/tags?workspaceId=${workspaceId}`, { // Backend uses req.query? No, backend implementation of listTags usually checks workspaceId from user or query. 
        // tasks.repository.js: listTags(workspaceId)
        // tasks.controller.js: listTags uses req.user.workspaceId ? Let's check controller. 
        // Based on other endpoints, it might rely on auth token context. 
        // BUT tasks.repository.js createTag takes workspaceId.
        // tasks.routes.js: router.get('/tags', tasksController.listTags);
        // Let's assume controller gets it from user context or we pass it?
        // Actually best to pass it in query if backend supports it.
        token,
        method: "GET",
    });
}

export async function assignTagApi({ token, workspaceId, taskId, tagId }) {
    return apiClient(`${BASE_TASKS}/${taskId}/tags`, {
        token,
        method: "POST",
        body: { tagId }
    });
}

export async function removeTagApi({ token, workspaceId, taskId, tagId }) {
    return apiClient(`${BASE_TASKS}/${taskId}/tags/${tagId}`, {
        token,
        method: "DELETE"
    });
}

export async function deleteTaskApi({ token, workspaceId, taskId, mode }) {
    const url = mode ? `${BASE_TASKS}/${taskId}?mode=${mode}` : `${BASE_TASKS}/${taskId}`;
    return apiClient(url, {
        token,
        method: "DELETE"
    });
}

// Reminders

export async function createReminderApi({ token, workspaceId, taskId, remindAt }) {
    return apiClient(`${BASE_TASKS}/${taskId}/reminders`, {
        token,
        method: "POST",
        body: { remindAt }
    });
}

export async function deleteReminderApi({ token, workspaceId, taskId, reminderId }) {
    return apiClient(`${BASE_TASKS}/${taskId}/reminders/${reminderId}`, {
        token,
        method: "DELETE"
    });
}

// Statuses

export async function listStatusesApi({ token, workspaceId }) {
    return apiClient(`${BASE_TASKS}/statuses?workspaceId=${workspaceId}`, {
        token,
        method: "GET"
    });
}

export async function createTaskStatusApi({ token, workspaceId, data }) {
    return apiClient(`${BASE_TASKS}/statuses?workspaceId=${workspaceId}`, {
        token,
        method: "POST",
        body: data
    });
}

export async function updateTaskStatusApi({ token, workspaceId, statusId, data }) {
    return apiClient(`/tasks/statuses/${statusId}`, {
        token,
        method: "PATCH",
        body: data
    });
}

export async function deleteTaskStatusApi({ token, workspaceId, statusId }) {
    return apiClient(`/tasks/statuses/${statusId}`, {
        token,
        method: "DELETE"
    });
}
